import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Sparkles, ChevronRight, Gift, BookOpen, Target, Play, Trophy, Heart, Award } from 'lucide-react';
import { apiClient } from '../../api/client';
import useCreditsLabel from '../../hooks/useCreditsLabel';

const CATEGORY_STYLES = {
  social: { gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-400/20 text-emerald-100' },
  career: { gradient: 'from-blue-500 to-indigo-600', badge: 'bg-blue-400/20 text-blue-100' },
  emotional: { gradient: 'from-rose-500 to-pink-600', badge: 'bg-rose-400/20 text-rose-100' },
  confidence: { gradient: 'from-amber-500 to-orange-600', badge: 'bg-amber-400/20 text-amber-100' },
};

const ICON_MAP = {
  school: BookOpen,
  briefcase: Target,
  heart: Heart,
  mic: Play,
  users: Trophy,
};

const QuestOfTheWeekCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [countdown, setCountdown] = useState('');
  const { label: creditsLabel, isAdultMode } = useCreditsLabel();

  useEffect(() => {
    apiClient.request('/api/enhancements/quest-of-the-week')
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!data?.seconds_until_rotation) return;
    const endTime = Date.now() + data.seconds_until_rotation * 1000;

    const tick = () => {
      const remaining = Math.max(0, endTime - Date.now());
      const d = Math.floor(remaining / 86400000);
      const h = Math.floor((remaining % 86400000) / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      if (d > 0) setCountdown(`${d}d ${h}h`);
      else if (h > 0) setCountdown(`${h}h ${m}m`);
      else setCountdown(`${m}m`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [data]);

  const claimBonus = async () => {
    setClaiming(true);
    try {
      await apiClient.request('/api/enhancements/quest-of-the-week/claim-bonus', { method: 'POST' });
      setData(prev => ({ ...prev, bonus_claimed: true }));
    } catch (e) {
      alert(e.message || 'Failed to claim bonus');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="quest-of-the-week-card" className="rounded-2xl overflow-hidden animate-pulse">
        <div className="h-48 bg-gray-200" />
      </div>
    );
  }

  if (!data) return null;

  const style = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.social;
  const IconComponent = ICON_MAP[data.icon] || BookOpen;
  const isCompleted = data.user_status === 'completed';
  const isActive = data.user_status === 'active';
  const progress = isActive ? Math.round((data.chapters_completed / data.chapters) * 100) : 0;

  return (
    <div data-testid="quest-of-the-week-card" className="rounded-2xl overflow-hidden">
      <div className={`bg-gradient-to-br ${style.gradient} p-5 relative`}>
        {/* Decorative sparkles */}
        <div className="absolute top-3 right-3 opacity-20">
          <Sparkles className="w-20 h-20 text-white" />
        </div>

        {/* Badge + timer */}
        <div className="flex items-center justify-between mb-4 relative z-10">
          <div className="flex items-center gap-2">
            <div className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-bold flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Quest of the Week
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.badge}`}>
              {data.difficulty}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-white/70 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>{countdown} left</span>
          </div>
        </div>

        {/* Quest info */}
        <div className="flex items-start gap-3 mb-4 relative z-10">
          <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white flex-shrink-0">
            <IconComponent className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-white leading-tight">{data.title}</h3>
            <p className="text-sm text-white/70 mt-0.5">{data.tagline}</p>
          </div>
        </div>

        {/* Rewards */}
        <div className="flex items-center gap-3 mb-4 relative z-10">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 backdrop-blur-sm">
            <Gift className="w-3.5 h-3.5 text-yellow-200" />
            <span className="text-sm font-bold text-white">{data.total_hearts}</span>
            <span className="text-xs text-white/60">{creditsLabel}</span>
          </div>
          <div className="px-2.5 py-1.5 rounded-lg bg-white/15 backdrop-blur-sm text-xs text-white/80">
            +{data.bonus_hearts} bonus
          </div>
          <div className="px-2.5 py-1.5 rounded-lg bg-white/15 backdrop-blur-sm text-xs text-white/80">
            {data.nft_rarity} NFT
          </div>
        </div>

        {/* Progress or CTA */}
        <div className="relative z-10">
          {isCompleted && !data.bonus_claimed && (
            <button
              data-testid="claim-qotw-bonus"
              onClick={claimBonus}
              disabled={claiming}
              className="w-full py-2.5 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Gift className="w-4 h-4" />
              {claiming ? 'Claiming...' : `Claim +${data.bonus_hearts} Bonus Hearts`}
            </button>
          )}

          {isCompleted && data.bonus_claimed && (
            <div className="w-full py-2.5 rounded-xl bg-white/15 text-white/80 font-medium text-sm text-center flex items-center justify-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-200" />
              Completed & Bonus Claimed
            </div>
          )}

          {isActive && (
            <div>
              <div className="flex items-center justify-between text-xs text-white/70 mb-1.5">
                <span>{data.chapters_completed}/{data.chapters} chapters</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
              <button
                data-testid="continue-qotw"
                onClick={() => navigate('/quests')}
                className="w-full py-2.5 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
              >
                Continue Quest <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {data.user_status === 'available' && (
            <button
              data-testid="start-qotw"
              onClick={() => navigate('/quests')}
              className="w-full py-2.5 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
            >
              Start This Quest <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestOfTheWeekCard;

